// errorClass — the failure-family classification over the domain's
// error taxonomy (.claude/analysis/error-handling.md §3), applied at the
// app→UI seam so a refusal is never flattened to "check your input".
//
// One family per canonical handling. NOT a replacement for the per-BC
// leaves — a classification OVER them: the leaf keeps its precise name
// (it rides on `External.Refused.errorType` for telemetry and per-leaf
// display overrides); the class picks the default message + recovery.
//
// `@bilda/core` is imported TYPE-ONLY (the diagnosticMessage precedent:
// a value import would pull core's runtime into the UI bundle). The
// classification table is typed `Record<Core.Error["type"], ErrorClass>`,
// so a NEW domain error leaf fails to compile here until it is
// classified — the compile-forced coverage the doc demands, without a
// runtime core dependency.
//
// The wire carries two vocabularies:
//   1. DOMAIN leaves (`stale_content_hash`, …) — usually nested as the
//      `cause` of a workflow wrapper (`rename_resource_refused`).
//      Classified by the exhaustive table below.
//   2. WORKFLOW leaves (`rename_resource_stale`,
//      `package_material_material_not_authored`, …) — @bilda/app's own
//      refusal vocabulary. These follow the same naming families, so
//      they classify by suffix, with a small explicit table for the
//      leaves whose name doesn't carry their family. Unknown types
//      return `undefined` and the caller keeps its generic fallback.

export type { DomainErrorType, ErrorClass };
export { classifyWireErrorType, isDomainErrorType, isInfraWireType };

import type * as Core from "@bilda/core";

// The closed sum of failure families — the canonical vocabulary every
// refusal maps onto. `specific` = a one-off leaf with no generic
// family; every `specific` leaf MUST carry its own display override
// (see externalError.ts).
type ErrorClass =
    | "not_found"
    | "already_exists"
    | "gone"
    | "not_deleted"
    | "invalid_input"
    | "conflict"
    | "in_use"
    | "not_permitted"
    | "precondition"
    | "specific";

type DomainErrorType = Core.Error["type"];

// Exhaustive by construction: `Record<DomainErrorType, …>` refuses to
// compile with a leaf missing OR an unknown key present.
const classByType: Record<DomainErrorType, ErrorClass> = {
    // === Workspace ===
    workspace_not_found: "not_found",
    workspace_already_exists: "already_exists",
    smart_filter_not_found: "not_found",
    smart_filter_already_exists: "already_exists",
    smart_filter_not_deleted: "not_deleted",
    smart_filter_name_blank: "invalid_input",
    smart_filter_name_taken: "already_exists",
    format_not_in_registry: "invalid_input",
    // === Scope ===
    scope_not_found: "not_found",
    scope_already_exists: "already_exists",
    scope_already_archived: "precondition",
    scope_not_archived: "precondition",
    scope_already_deleted: "gone",
    scope_not_deleted: "not_deleted",
    // === Project ===
    project_not_found: "not_found",
    project_already_exists: "already_exists",
    project_title_empty: "invalid_input",
    project_title_too_long: "invalid_input",
    project_description_empty: "invalid_input",
    project_description_too_long: "invalid_input",
    project_audience_missing: "not_found",
    audience_already_on_project: "already_exists",
    audience_not_on_project: "precondition",
    project_archived_read_only: "precondition",
    project_already_archived: "precondition",
    project_not_archived: "precondition",
    project_removed_already: "gone",
    canvas_target_missing: "not_found",
    already_on_project_canvas: "already_exists",
    not_on_project_canvas: "precondition",
    // === Association ===
    resource_already_in_scope: "precondition",
    resource_not_in_scope: "precondition",
    role_already_granted: "precondition",
    role_not_granted: "precondition",
    role_already_defined: "already_exists",
    role_not_defined: "not_found",
    // === Resource ===
    resource_not_found: "not_found",
    resource_already_exists: "already_exists",
    resource_already_deleted: "gone",
    resource_not_deleted: "not_deleted",
    // Archive lifecycle (F86): the resource is in the wrong state for the
    // requested archive verb (already archived / not archived).
    resource_already_archived: "precondition",
    resource_not_archived: "precondition",
    resource_kind_not_editable: "not_permitted",
    export_immutable: "not_permitted",
    internal_provenance_refused_as_source: "not_permitted",
    content_hash_already_exists: "conflict",
    promotion_target_not_file: "precondition",
    stale_content_hash: "conflict",
    // Restore's freshness guard — the current head moved between opening
    // history and clicking restore; an optimistic-concurrency conflict, the
    // same family as `stale_content_hash`.
    stale_restore: "conflict",
    // === Resource / versions (lineage) ===
    resource_version_not_found: "not_found",
    resource_draft_base_moved: "conflict",
    resource_draft_unchanged: "precondition",
    resource_version_content_mismatch: "invalid_input",
    // === Resource / references ===
    reference_target_not_found: "not_found",
    reference_target_version_not_found: "not_found",
    include_cycle: "precondition",
    reference_not_found: "not_found",
    reference_not_behind: "precondition",
    builds_on_cycle: "precondition",
    builds_on_ancestor_or_descendant: "precondition",
    builds_on_already_present: "already_exists",
    builds_on_not_found: "not_found",
    builds_on_not_behind: "precondition",
    // === Resource / review ===
    resource_version_approval_requires_author: "not_permitted",
    resource_version_not_newest_head: "conflict",
    resource_planned_not_reviewable: "precondition",
    resource_not_planned: "precondition",
    brief_revise_after_generation_not_yet: "precondition",
    include_element_edit_refused: "invalid_input",
    // === Resource / edit ===
    element_path_empty: "invalid_input",
    element_not_found: "not_found",
    content_unparseable: "specific",
    element_already_exists_at_path: "already_exists",
    duplicate_id_in_subtree: "invalid_input",
    element_not_a_container: "invalid_input",
    move_target_inside_source: "invalid_input",
    // === Conversation ===
    conversation_not_found: "not_found",
    conversation_already_exists: "already_exists",
    conversation_is_deleted: "gone",
    conversation_already_deleted: "gone",
    conversation_not_deleted: "not_deleted",
    conversation_already_archived: "precondition",
    conversation_not_archived: "precondition",
    elicitation_request_not_found: "not_found",
    tool_call_not_found: "not_found",
    tool_call_already_completed: "precondition",
    tool_result_already_recorded: "precondition",
    message_id_already_used: "already_exists",
    tool_call_id_already_used: "already_exists",
    outstanding_tool_calls: "precondition",
    title_empty: "invalid_input",
    turn_not_found: "not_found",
    turn_already_closed: "precondition",
    not_awaiting_source: "precondition",
    conversation_goal_already_added: "already_exists",
    conversation_goal_not_found: "not_found",
    // === Learning / concept ===
    concept_not_found: "not_found",
    concept_already_exists: "already_exists",
    concept_already_deleted: "gone",
    concept_not_deleted: "not_deleted",
    concept_label_empty: "invalid_input",
    // === Learning / objective ===
    objective_not_found: "not_found",
    objective_already_exists: "already_exists",
    objective_already_deleted: "gone",
    objective_not_deleted: "not_deleted",
    objective_statement_empty: "invalid_input",
    // === Learning / term ===
    term_not_found: "not_found",
    term_already_exists: "already_exists",
    term_already_deleted: "gone",
    term_not_deleted: "not_deleted",
    term_text_empty: "invalid_input",
    term_definition_empty: "invalid_input",
    term_already_defined: "already_exists",
    term_not_defined: "conflict",
    term_definition_unchanged: "conflict",
    definition_claim_not_found: "not_found",
    // === Learning / context ===
    learning_context_not_found: "not_found",
    learning_context_already_exists: "already_exists",
    learning_context_already_deleted: "gone",
    learning_context_not_deleted: "not_deleted",
    learning_context_title_empty: "invalid_input",
    learning_context_title_too_long: "invalid_input",
    learning_context_purpose_empty: "invalid_input",
    learning_context_approval_words_empty: "invalid_input",
    learning_context_purpose_not_set: "precondition",
    skill_already_declared: "precondition",
    skill_not_declared: "precondition",
    skill_label_empty: "invalid_input",
    empty_instruction_language_profiles: "invalid_input",
    empty_learner_language_profiles: "invalid_input",
    objective_already_attached: "precondition",
    objective_not_attached: "precondition",
    concept_already_attached: "precondition",
    concept_not_attached: "precondition",
    learner_profile_already_attached: "precondition",
    learner_profile_not_attached: "precondition",
    instruction_language_profile_already_attached: "precondition",
    instruction_language_profile_not_attached: "precondition",
    learner_language_profile_already_attached: "precondition",
    learner_language_profile_not_attached: "precondition",
    last_instruction_language_profile: "in_use",
    last_learner_language_profile: "in_use",
    // === Learning / learner profile ===
    learner_profile_not_found: "not_found",
    learner_profile_already_exists: "already_exists",
    learner_profile_already_deleted: "gone",
    learner_profile_not_deleted: "not_deleted",
    learner_profile_label_empty: "invalid_input",
    // === Language ===
    language_profile_not_found: "not_found",
    language_profile_already_exists: "already_exists",
    language_profile_already_deleted: "gone",
    language_profile_not_deleted: "not_deleted",
    language_profile_in_use: "in_use",
    language_variety_empty: "invalid_input",
    language_level_empty: "invalid_input",
    terminology_domain_empty: "invalid_input",
    language_tone_empty: "invalid_input",
    // === Packaging ===
    packaging_not_found: "not_found",
    invocation_already_exists: "already_exists",
    invocation_not_found: "not_found",
    invocation_not_running: "precondition",
    adapter_missing: "specific",
    adapter_failed: "specific",
    // === KnowledgeBank ===
    relation_name_empty: "invalid_input",
    broader_parent_exists: "conflict",
    broader_cycle: "conflict",
    related_to_itself: "invalid_input",
    claim_already_recorded: "already_exists",
    claim_not_found: "not_found",
    claim_wording_empty: "invalid_input",
    claim_source_not_found: "not_found",
    claim_source_not_readable: "conflict",
    claim_quote_too_short: "invalid_input",
    claim_quote_not_found: "not_found",
    // === Annotation ===
    annotation_not_found: "not_found",
    annotation_already_exists: "already_exists",
    annotation_already_deleted: "gone",
    annotation_not_deleted: "not_deleted",
    annotation_target_not_found: "not_found",
    annotation_payload_empty: "invalid_input",
    annotation_target_version_missing: "conflict",
    annotation_kind_mismatch: "precondition",
    understanding_already_pinned: "already_exists",
    understanding_entry_not_found: "not_found",
    understanding_entry_locked: "precondition",
    understanding_entry_duplicate: "already_exists",
    pinned_version_not_found: "not_found",
    pinned_extract_mismatch: "conflict",
    passage_quote_mismatch: "invalid_input",
    // === Proposal ===
    proposal_already_exists: "already_exists",
    proposal_not_found: "not_found",
    proposal_not_pending: "precondition",
    proposal_rationale_empty: "invalid_input",
    proposal_target_not_found: "not_found",
    proposal_target_not_material: "precondition",
    // === Journal ===
    journal_summary_locked: "precondition",
    journal_own_text_reading_not_allowed: "not_permitted",
    journal_summary_not_found: "not_found",
    journal_summary_not_locked: "precondition",
    journal_own_text_not_found: "not_found",
};

const isDomainErrorType = (s: string): s is DomainErrorType =>
    Object.hasOwn(classByType, s);

// Workflow leaves whose name doesn't carry their family — explicit.
const workflowClassByType: Readonly<Record<string, ErrorClass>> = {
    // NOTE: `add_source_refused_duplicate_content` is retired (DB4,
    // restoring IDD `82fd6b99`) — a byte-identical source re-add is now
    // transparent reuse (`AddSource.AddSourceOutput`'s `ReusedExisting`
    // leaf), never an error this table needs to classify. The FILE-path
    // duplicate error stays: a File has no cross-workspace dedupe
    // invariant to alias against.
    add_file_refused_duplicate_content: "conflict",
    add_source_bytes_are_opaque: "invalid_input",
    add_source_extract_failed: "invalid_input",
    add_source_kind_not_accepted: "invalid_input",
    package_material_material_not_authored: "precondition",
    package_material_packaging_not_available: "specific",
    package_material_parts_unfinished: "specific",
    package_material_parts_empty: "specific",
    package_material_format_not_offered: "specific",
    package_material_render_failed: "specific",
    export_package_not_packaged: "precondition",
    export_package_target_not_format: "invalid_input",
    // Generation's start refusals (the Studio desk words each in place).
    generation_root_ungrounded: "precondition",
    generation_job_already_active: "conflict",
    generate_from_brief_not_planned: "precondition",
    generate_from_brief_not_in_container: "precondition",
    bad_input: "invalid_input",
};

// Suffix families over @bilda/app's workflow-error naming convention.
// Checked in order; first match wins.
const classBySuffix: readonly (readonly [string, ErrorClass])[] = [
    ["_not_found", "not_found"],
    ["_already_exists", "already_exists"],
    ["_already_deleted", "gone"],
    ["_not_deleted", "not_deleted"],
    ["_stale", "conflict"],
    ["_empty", "invalid_input"],
    ["_empty_path", "invalid_input"],
    ["_kind_mismatch", "precondition"],
    ["_wrong_kind", "precondition"],
    ["_not_a_material", "precondition"],
    ["_ineligible", "precondition"],
    ["_not_pending", "precondition"],
];

// Classify one wire discriminant. Domain table first (compile-forced,
// precise), then the workflow table, then the suffix families.
// `undefined` = not a refusal this vocabulary knows; the caller keeps
// its generic fallback.
const classifyWireErrorType = (t: string): ErrorClass | undefined => {
    if (isDomainErrorType(t)) return classByType[t];
    const explicit = workflowClassByType[t];
    if (explicit !== undefined) return explicit;
    return classBySuffix.find(([suffix]) => t.endsWith(suffix))?.[1];
};

// Wire families that are server-side infrastructure faults (event-store
// append, PM cascade, adapter/port crashes) — not domain refusals and
// not the user's input. The honest surface is "the server failed —
// retry" (`External.Upstream`), never "check your input".
const INFRA_SUFFIXES: readonly string[] = [
    "_append_failed",
    "_cascade_exceeded",
    "_port_failed",
    "_render_failed",
    "_agent_failed",
    "_resolve_failed",
    "_codec_failed",
    "_apply_failed",
    "_parse_failed",
    "_boot_failed",
];

const isInfraWireType = (t: string): boolean =>
    INFRA_SUFFIXES.some((suffix) => t.endsWith(suffix));
